import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, Stack, Alert, TextField, FormControlLabel, Switch, Divider } from '@mui/material';
import { CloudUpload, Download } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import TopNav from './TopNav.jsx';
import Footer from './Footer.jsx';
import GlassCard from './GlassCard.jsx';
import CategoryChips from './CategoryChips.jsx';
import ActionButton from './ActionButton.jsx';
import ShareControl from './ShareControl.jsx';
import SaveMapButton from './SaveMapButton.jsx';
import QuickOrderStrip from './QuickOrderStrip.jsx';
import TrailMapRender, { TILE_STYLES, STAT_FIELD_DEFS } from './trail/TrailMapRender.jsx';
import { getMapForReopen } from '../utils/mapsRepository';
import { makeThumbnail } from '../utils/mapThumbnail';
import { useAuth } from '../auth/AuthContext';
import { parseGpx, aggregateStats, formatDistance } from '../utils/gpx';
import useDocumentTitle from '../utils/useDocumentTitle';
import { storage } from '../firebaseApp';
import { orderProduct } from '../actions/shapeActions';
import { trackDownload, trackAddToCart } from '../utils/analytics';
import { trackFbDownload, trackFbAddToCart } from '../utils/facebookPixel';
import { trackPinDownload, trackPinAddToCart } from '../utils/pinterestTag';
import { PALETTE } from '../theme';

const TRACK_LABEL = 'Trail Map';

const STYLE_OPTIONS = TILE_STYLES.map((s) => ({ key: s.id, text: s.label, value: s.id }));
const UNIT_OPTIONS = [{ key: 'metric', text: 'km / m', value: 'metric' }, { key: 'imperial', text: 'mi / ft', value: 'imperial' }];
const SHOW_TOGGLES = [{ key: 'title', label: 'Title' }, { key: 'stats', label: 'Stats' }, { key: 'elevation', label: 'Elevation' }, { key: 'markers', label: 'Start / end' }];

const SectionLabel = ({ children }) => (
    <Typography sx={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', color: PALETTE.textMuted, mb: 1 }}>{children}</Typography>
);

/**
 * Trail Maps — turn a GPS track (one file, or several for a multi-day hike)
 * into a printable map, orderable on products. Same chrome + components as the
 * rest of the site (TopNav / Footer / GlassCard / CategoryChips / QuickOrderStrip).
 */
const TrailMapsPage = () => {
    useDocumentTitle('Trail Maps — Map Shaper');
    const dispatch = useDispatch();
    const { authReady, isSignedIn } = useAuth();
    const [segments, setSegments] = useState([]);
    const [title, setTitle] = useState('');
    const [units, setUnits] = useState('metric');
    const [styleId, setStyleId] = useState('topographic');
    const [show, setShow] = useState({ title: true, stats: true, elevation: true, markers: true });
    const [statFields, setStatFields] = useState({ distance: true, gain: true, days: true, high: true, duration: true, loss: false, low: false });
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [designUrl, setDesignUrl] = useState(null);
    const [openMap, setOpenMap] = useState(null); // saved map currently open (?open=<id>)
    const renderRef = useRef(null);

    const loadFiles = useCallback((entries) => {
        setError('');
        try {
            const segs = entries.map(({ text, name }) => parseGpx(text, name.replace(/\.(gpx|tcx)$/i, '')));
            segs.sort((a, b) => {
                const ta = a.points[0]?.time;
                const tb = b.points[0]?.time;
                if (ta != null && tb != null) return ta - tb;
                return a.name.localeCompare(b.name);
            });
            setSegments(segs);
        } catch (e) {
            setError(e.message || 'Could not read that file.');
        }
    }, []);

    const ingest = useCallback(async (fileList) => {
        const files = Array.from(fileList || []).filter((f) => /\.(gpx|tcx)$/i.test(f.name));
        if (!files.length) { setError('Please choose GPX files (Garmin, Strava, Komoot, AllTrails…).'); return; }
        const entries = await Promise.all(files.map(async (f) => ({ text: await f.text(), name: f.name })));
        if (files.length === 1) setTitle(files[0].name.replace(/\.(gpx|tcx)$/i, ''));
        loadFiles(entries);
    }, [loadFiles]);

    // Live design thumbnail for product mockups. Debounced and triggered by
    // BOTH overlay-state changes AND the render's onReady (map 'idle' → tiles
    // painted). After a basemap switch the onReady fire is the last trigger, so
    // the final capture is post-paint (never a blank half-loaded frame).
    const capTimer = useRef(null);
    const captureDesign = useCallback(() => {
        clearTimeout(capTimer.current);
        capTimer.current = setTimeout(async () => {
            try {
                const blob = await renderRef.current?.capture();
                if (blob) setDesignUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
            } catch { /* preview is best-effort */ }
        }, 450);
    }, []);
    useEffect(() => { if (segments.length) captureDesign(); }, [segments, title, units, styleId, show, statFields, captureDesign]);

    useEffect(() => () => { if (designUrl) URL.revokeObjectURL(designUrl); }, [designUrl]);

    const captureBlob = useCallback(() => renderRef.current.capture(), []);

    // capture → Firebase upload → public URL (what QuickOrderStrip orders on).
    const prepareImage = useCallback((onProgress) => new Promise(async (resolve, reject) => {
        try {
            const blob = await captureBlob();
            const id = Math.random().toString(36).substring(2) + Date.now().toString(36) + '.png';
            const task = uploadBytesResumable(ref(storage, 'images/' + id), blob);
            task.on('state_changed',
                (snap) => onProgress && onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
                reject,
                async () => { try { resolve(await getDownloadURL(task.snapshot.ref)); } catch (e) { reject(e); } });
        } catch (e) { reject(e); }
    }), [captureBlob]);

    const onOrder = useCallback(({ product, label, imageUrl }) => {
        const days = segments.length;
        trackAddToCart({ product: product.analyticsKey, kind: 'trail', mapType: 'trail', visitedCount: days, totalCount: days });
        trackFbAddToCart(product.name, TRACK_LABEL);
        trackPinAddToCart(product.name, TRACK_LABEL);
        dispatch(orderProduct(imageUrl, product, label));
    }, [dispatch, segments.length]);

    const onDownload = async () => {
        try {
            setBusy(true);
            trackDownload({ kind: 'trail', mapType: 'trail', visitedCount: segments.length, totalCount: segments.length });
            trackFbDownload(TRACK_LABEL);
            trackPinDownload(TRACK_LABEL);
            const blob = await captureBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(title || 'trail-map').replace(/[^\w-]+/g, '-').toLowerCase()}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            setError('Could not export the map. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // ── Saved-map adapter ──────────────────────────────────────────────────
    // The parsed GPS tracks are the heavy payload → Storage JSON; everything
    // else is small settings. On reopen we restore the tracks verbatim (no need
    // to keep the original GPX text).
    const getSnapshot = useCallback(async () => {
        let thumbnailBlob = null;
        try {
            thumbnailBlob = await makeThumbnail(await captureBlob());
        } catch {
            /* thumbnail is best-effort */
        }
        const payload = new Blob([JSON.stringify(segments)], { type: 'application/json' });
        return {
            type: 'trail',
            defaultName: title || 'Trail',
            breadcrumb: title ? ['Trail', title] : ['Trail'],
            settings: { title, units, styleId, show, statFields },
            assets: { payload },
            thumbnailBlob,
        };
    }, [captureBlob, segments, title, units, styleId, show, statFields]);

    // Reopen a saved trail (My Maps → ?open=<id>). Waits for Firebase to restore
    // the session first, so a cold load / refresh of the URL works too.
    const reopenedRef = useRef(false);
    useEffect(() => {
        if (reopenedRef.current || !authReady || !isSignedIn) return;
        const id = new URLSearchParams(window.location.search).get('open');
        if (!id) return;
        reopenedRef.current = true;
        (async () => {
            try {
                const rec = await getMapForReopen(id);
                if (rec.type !== 'trail') return;
                const s = rec.settings || {};
                if (Array.isArray(rec.payload)) setSegments(rec.payload);
                if (s.title != null) setTitle(s.title);
                if (s.units) setUnits(s.units);
                if (s.styleId) setStyleId(s.styleId);
                if (s.show) setShow(s.show);
                if (s.statFields) setStatFields(s.statFields);
                setOpenMap({ id: rec.id, name: rec.name });
            } catch (e) {
                setError(e.message || 'Could not load that saved map.');
            }
        })();
    }, [authReady, isSignedIn]);

    const toggleShow = (key) => setShow((p) => ({ ...p, [key]: !p[key] }));
    const toggleStat = (key) => setStatFields((p) => ({ ...p, [key]: !p[key] }));

    const stats = segments.length ? aggregateStats(segments) : null;
    const caption = stats ? `${title} · ${formatDistance(stats.distanceM, units)}` : title;

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: PALETTE.page }}>
            <TopNav />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
                    <Typography component="h1" sx={{ fontSize: { xs: '1.9rem', sm: '2.5rem' }, fontWeight: 800, color: PALETTE.text, letterSpacing: '-0.02em', lineHeight: 1.1, mb: 1 }}>Trail Maps</Typography>
                    <Typography sx={{ color: PALETTE.textBody, fontSize: { xs: '0.95rem', sm: '1.05rem' }, maxWidth: 560, mx: 'auto', lineHeight: 1.5 }}>
                        Turn a GPS track into a map — download the image, or order it on a cup, t-shirt and more. Upload one file, or several for a multi-day hike, exported from Garmin, Strava, Komoot or any app.
                    </Typography>
                </Box>

                <Box onDrop={(e) => { e.preventDefault(); ingest(e.dataTransfer.files); }} onDragOver={(e) => e.preventDefault()} component="label"
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, p: { xs: 2.5, sm: 3 }, mb: 3, cursor: 'pointer', textAlign: 'center', border: `2px dashed ${PALETTE.textVeryFaint}`, borderRadius: 3, backgroundColor: PALETTE.surface, transition: 'border-color .15s, background-color .15s', '&:hover': { borderColor: PALETTE.primary, backgroundColor: PALETTE.primary50 } }}>
                    <input hidden type="file" accept=".gpx,.tcx,application/gpx+xml" multiple onChange={(e) => ingest(e.target.files)} />
                    <CloudUpload sx={{ color: PALETTE.primary, fontSize: 32 }} />
                    <Typography sx={{ fontWeight: 700, color: PALETTE.text }}>Drop GPX files here, or click to browse</Typography>
                    <Typography variant="caption" sx={{ color: PALETTE.textMuted }}>Multiple files stitch into one multi-day trail · GPX / TCX</Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

                {segments.length > 0 && (
                    <>
                        <GlassCard variant="frame" sx={{ mb: 3, p: { xs: 2, sm: 2.5 } }}>
                            <Stack spacing={2.5}>
                                <TextField label="Map title" value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth inputProps={{ maxLength: 48 }} helperText="Shown centered on the map." />

                                <Box>
                                    <SectionLabel>MAP STYLE</SectionLabel>
                                    <CategoryChips value={styleId} onChange={setStyleId} options={STYLE_OPTIONS} sx={{ justifyContent: 'flex-start' }} />
                                </Box>

                                <Box>
                                    <SectionLabel>UNITS</SectionLabel>
                                    <CategoryChips value={units} onChange={setUnits} options={UNIT_OPTIONS} sx={{ justifyContent: 'flex-start' }} />
                                </Box>

                                <Box>
                                    <SectionLabel>SHOW (on the map)</SectionLabel>
                                    <Stack direction="row" flexWrap="wrap" sx={{ gap: { xs: 0, sm: 1 } }}>
                                        {SHOW_TOGGLES.map((t) => (
                                            <FormControlLabel key={t.key} control={<Switch size="small" checked={!!show[t.key]} onChange={() => toggleShow(t.key)} />} label={<Typography sx={{ fontSize: 14, color: PALETTE.textBody }}>{t.label}</Typography>} />
                                        ))}
                                    </Stack>
                                </Box>

                                {show.stats && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <SectionLabel>STATS TO SHOW</SectionLabel>
                                            <Stack direction="row" flexWrap="wrap" sx={{ gap: { xs: 0, sm: 1 } }}>
                                                {STAT_FIELD_DEFS.map((f) => (
                                                    <FormControlLabel key={f.key} control={<Switch size="small" checked={!!statFields[f.key]} onChange={() => toggleStat(f.key)} />} label={<Typography sx={{ fontSize: 14, color: PALETTE.textBody }}>{f.label}</Typography>} />
                                                ))}
                                            </Stack>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </GlassCard>

                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <TrailMapRender ref={renderRef} segments={segments} title={title} units={units} styleId={styleId} show={show} statFields={statFields} onReady={captureDesign} />
                        </Box>

                        {/* products + download + share — same components as Travel Maps */}
                        <QuickOrderStrip defaultLabel={caption} prepareImage={prepareImage} onOrder={onOrder} designSrc={designUrl} />

                        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 }, mb: 4 }}>
                            <ActionButton variant="ghost" startIcon={<Download sx={{ fontSize: 18 }} />} onClick={onDownload} disabled={busy} loading={busy} sx={{ width: 'auto', minWidth: 0, px: 1.5 }}>
                                Download
                            </ActionButton>
                            <SaveMapButton
                                getSnapshot={getSnapshot}
                                openMap={openMap}
                                onSaved={setOpenMap}
                                onStatus={(msg, variant) => (variant === 'error' ? setError(msg) : setNotice(msg))}
                                variant="ghost"
                                sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                            />
                            <ShareControl
                                kind="trail"
                                trailTitle={title}
                                trailDistance={stats ? formatDistance(stats.distanceM, units) : ''}
                                trailDays={stats ? stats.days : 1}
                                getBlob={captureBlob}
                                fileName={`${(title || 'trail-map').replace(/[^\w-]+/g, '-').toLowerCase()}.png`}
                                trackId="trail_map"
                                variant="ghost"
                                sx={{ width: 'auto', minWidth: 0, px: 1.5 }}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <Footer />
        </Box>
    );
};

export default TrailMapsPage;
